import { PDFDocument, degrees } from "pdf-lib";

const LADO_MAXIMO_PX = 2400; // mesmo limite de imagemParaPdf/recortePerspectiva

/** Soma um giro (múltiplo de 90°, positivo = horário) e normaliza para 0, 90, 180 ou 270. */
export function somarRotacao(atual, delta) {
    return (((atual + delta) % 360) + 360) % 360;
}

/**
 * Gira os 4 cantos do recorte junto com a imagem (90° horário ou anti-horário), mantendo a ordem
 * superior esquerdo, superior direito, inferior direito, inferior esquerdo — o recorte já marcado continua
 * sobre a mesma área do documento.
 */
export function rotacionarPontos(pontos, delta) {
    if (delta === 90) {
        const r = p => ({ x: 1 - p.y, y: p.x });
        return [r(pontos[3]), r(pontos[0]), r(pontos[1]), r(pontos[2])];
    }
    if (delta === -90) {
        const r = p => ({ x: p.y, y: 1 - p.x });
        return [r(pontos[1]), r(pontos[2]), r(pontos[3]), r(pontos[0])];
    }
    throw new Error("Rotação suportada: 90° ou -90°.");
}

/**
 * Gira uma imagem em 90/180/270° (horário). Parte sempre da imagem original com o giro total — girar várias
 * vezes não acumula perda de qualidade. Devolve PNG se a origem for PNG, senão JPEG.
 */
export async function rotacionarImagem(blobImagem, graus) {
    const bitmap = await createImageBitmap(blobImagem, { imageOrientation: "from-image" });
    const escala = Math.min(1, LADO_MAXIMO_PX / Math.max(bitmap.width, bitmap.height));
    const largura = Math.round(bitmap.width * escala);
    const altura = Math.round(bitmap.height * escala);
    const deitada = graus === 90 || graus === 270;
    const canvas = document.createElement("canvas");
    canvas.width = deitada ? altura : largura;
    canvas.height = deitada ? largura : altura;
    const ctx = canvas.getContext("2d");
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((graus * Math.PI) / 180);
    ctx.drawImage(bitmap, -largura / 2, -altura / 2, largura, altura);
    bitmap.close?.();

    const ehPng = blobImagem.type === "image/png";
    return new Promise((resolve, reject) => canvas.toBlob(
        blob => (blob ? resolve(blob) : reject(new Error("Não foi possível girar a imagem."))),
        ehPng ? "image/png" : "image/jpeg",
        0.95,
    ));
}

/** Gira todas as páginas de um PDF em 90/180/270° (horário), somando ao giro que a página já tiver. */
export async function rotacionarPdf(blobPdf, graus) {
    let pdf;
    try {
        pdf = await PDFDocument.load(await blobPdf.arrayBuffer());
    } catch {
        throw new Error("Não foi possível girar o PDF (protegido por senha ou danificado).");
    }
    pdf.getPages().forEach(pagina => pagina.setRotation(degrees(somarRotacao(pagina.getRotation().angle, graus))));
    return new Blob([await pdf.save()], { type: "application/pdf" });
}
